import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { supabase } from "@/lib/supabase/supabaseClient";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import {
	ClientEntityType,
	FileWithShare,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	SupabaseRpcEnum,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { DefaultValues, TpTier } from "@/dentlab/src/types/enums";
import { goBackOrForwardDays } from "@/dentlab/src/lib/utils/utils-functions";
import { JobDocumentTypeEnum } from "@/dentlab/src/pages/job-page/job-document/job-document.types";
import { PermissionStatus } from "@/dentlab/src/hooks/actions/actions-hooks-types";
import { jobDocumentTypeNamePresets } from "@/dentlab/src/store/Jobs/utils";
import { getFileType } from "@/lib/utils/file-utils";

type extendedJobDocumentEntityType = JobDocumentEntityType & {
	job_items: JobItemEntityType[];
};

export interface JobSlice {
	jobDocuments: extendedJobDocumentEntityType[];
	labImages: FileWithShare[];
	labFiles: FileWithShare[];
	medImages: FileWithShare[];
	medFiles: FileWithShare[];
	initialize: () => Promise<void>;
	createJobWithDeliveryNote: (
		clientId: string,
		patientId?: string,
		title?: string
	) => Promise<{
		job: JobEntityType | null;
		deliveryNote: JobDocumentEntityType | null;
	}>;
	createJobDocument: (
		jobId: number,
		clientId: string,
		jobDocumentType: JobDocumentTypeEnum
	) => Promise<JobDocumentEntityType | null>;
	fetchJobDocumentsWithItems: (jobId: number) => Promise<void>;
	getDefaultValues: (
		organization: OrganizationEntityType,
		client: ClientEntityType
	) => Promise<{
		tpValueForJob: number;
		tpTierForJob: TpTier;
		tpVariationForJob: number;
		taxRateForJob: number;
	}>;
	isCreateJobDocumentAllowed: (
		jobDocumentType: JobDocumentTypeEnum
	) => PermissionStatus;
	fetchJobFiles: (jobId: number) => Promise<void>;
	shareFile: (fileId: string, jobId: number) => Promise<void>;
	unshareFile: (fileId: string, jobId: number) => Promise<void>;
}

export const createJobStore: StateCreator<JobSlice> = (set, get) => ({
	labImages: [],
	labFiles: [],
	medImages: [],
	medFiles: [],
	jobDocuments: [],
	organization: null,
	client: null,
	initialize: async () => {
		const { getSession, getProfile, getOrganization } =
			useCentralStore.getState();

		const userId = await getSession();
		if (!userId) return;
		await getProfile(userId);
		await getOrganization();
	},
	createJobWithDeliveryNote: async (
		clientId: string,
		patientId?: string,
		title?: string
	) => {
		const { organization, clientsLookup } = useCentralStore.getState();

		const client = clientsLookup[clientId];

		if (!client || !organization || !organization?.id) {
			showNotification({
				message:
					"Fehler beim Erstellen des Auftrags durch fehlende Daten.",
				type: "error",
			});
			Logger.error(
				"Unexpected error creating job. Missing client or organization data."
			);
			return { job: null, deliveryNote: null };
		}

		const {
			tpValueForJob,
			tpTierForJob,
			tpVariationForJob,
			taxRateForJob,
		} = await get().getDefaultValues(organization, client);

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.insert({
				code: "", // If code = '' it will be set on insert on the db
				client_id: client.id,
				title: title ?? "Neuer Auftrag",
				status: JobStatusEnum.IN_PROGRESS,
				organization_id: organization?.id,
				incoming_order_date: new Date().toISOString(),
				outgoing_order_date: goBackOrForwardDays(7),
				tax_rate: taxRateForJob,
				guarantor_id: client.guarantor_id,
				patient_id: patientId ?? null,
				tp_tier: tpTierForJob,
				tp_value: tpValueForJob,
				tp_variation: tpVariationForJob,
			})
			.select()
			.single();

		if (error || !data) {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			Logger.error(error);
			return { job: null, deliveryNote: null };
		}

		const job = data as JobEntityType;

		if (!job.id) {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			return { job: null, deliveryNote: null };
		}

		const deliveryNote = await get().createJobDocument(
			job.id,
			clientId,
			JobDocumentTypeEnum.DeliveryNote
		);

		return { job, deliveryNote };
	},
	createJobDocument: async (
		jobId: number,
		clientId: string,
		jobDocumentType: JobDocumentTypeEnum
	) => {
		const { clientsLookup } = useCentralStore.getState();

		const client = clientsLookup[clientId];

		if (!client || !jobId) {
			showNotification({
				message: "Fehler beim Erstellen des Lieferscheins",
				type: "error",
			});
			Logger.error(
				"Error creating delivery note. Missing client or job id."
			);
			return null;
		}

		await get().fetchJobDocumentsWithItems(jobId);

		const isAllowed = get().isCreateJobDocumentAllowed(jobDocumentType);

		if (!isAllowed.isAllowed) {
			showNotification({
				message: isAllowed.explanation,
				type: "error",
			});
			return null;
		}

		const count = get().jobDocuments.filter(
			(jobDocument) => jobDocument.type === jobDocumentType
		).length;

		const { data: deliveryNoteData, error: deliveryNoteError } =
			await supabase
				.from(SupabaseTableEnum.JOB_DOCUMENTS)
				.insert({
					title: `${jobDocumentTypeNamePresets(jobDocumentType)} ${
						count > 0 ? `(${count + 1})` : ""
					}`,
					job_id: jobId,
					type: jobDocumentType,
					discount_material: client?.discount_material ?? 0,
					discount_work: client?.discount_work ?? 0,
				} as JobDocumentEntityType)
				.select()
				.single();

		if (deliveryNoteError || !deliveryNoteData) {
			Logger.error(deliveryNoteError);
			showNotification({
				message: "Fehler beim Erstellen des Lieferscheins",
				type: "error",
			});
			return null;
		}

		const deliveryNote = deliveryNoteData as JobDocumentEntityType;

		await get().fetchJobDocumentsWithItems(jobId);

		return deliveryNote;
	},
	fetchJobDocumentsWithItems: async (jobId: number) => {
		if (!jobId) {
			Logger.error("No job id provided");
			showNotification({
				message: "Fehler beim Abrufen von Auftragsdokumenten",
				type: "error",
			});
			return;
		}

		const jobDocuments: extendedJobDocumentEntityType[] = [];

		const { data, error } = await supabase
			.from("job_documents")
			.select()
			.eq("job_id", jobId)
			.is("deleted_at", null);

		if (error || !data) {
			Logger.error(error, {}, "Error fetching job documents");
			showNotification({
				message: "Fehler beim Abrufen von Auftragsdokumenten",
				type: "error",
			});
			return;
		}

		for (const jobDocument of data) {
			const { data: jobItemsData, error: jobItemsError } = await supabase
				.from("job_items")
				.select()
				.eq("job_document_id", jobDocument.id);

			if (jobItemsError || !jobItemsData) {
				Logger.error(jobItemsError, {}, "Error fetching job items");
				showNotification({
					message: "Fehler beim Abrufen von Auftragspositionen",
					type: "error",
				});
				continue;
			}

			jobDocuments.push({
				...jobDocument,
				job_items: jobItemsData as JobItemEntityType[],
			});
		}

		set({ jobDocuments: jobDocuments });
	},
	getDefaultValues: async (
		organization: OrganizationEntityType,
		client: ClientEntityType
	) => {
		const taxRateForJob =
			organization?.mwst_percentage ?? DefaultValues.TAX_RATE;
		const tpValueForJob =
			client?.default_tp_value ??
			organization?.default_tp_value ??
			DefaultValues.TP_VALUE;
		const tpVariationForJob =
			client?.default_tp_variation ?? DefaultValues.TP_VARIATION;
		const tpTierForJob =
			client?.default_tp_tier ??
			organization?.default_tp_tier ??
			DefaultValues.TP_TIER;

		return {
			tpValueForJob,
			tpTierForJob,
			tpVariationForJob,
			taxRateForJob,
		};
	},
	isCreateJobDocumentAllowed: (
		jobDocumentType: JobDocumentTypeEnum
	): PermissionStatus => {
		const jobDocuments = get().jobDocuments;

		if (
			jobDocumentType === JobDocumentTypeEnum.Quotation ||
			jobDocumentType === JobDocumentTypeEnum.CreditNote ||
			jobDocumentType === JobDocumentTypeEnum.Tracing
		) {
			return {
				isAllowed: true,
				explanation: "",
			};
		} else if (jobDocumentType === JobDocumentTypeEnum.DeliveryNote) {
			const deliveryNoteExists = Object.values(jobDocuments).some(
				(jobDocument) =>
					jobDocument.type === JobDocumentTypeEnum.DeliveryNote &&
					jobDocument.status !== JobStatusEnum.ARCHIVED
			);

			if (!deliveryNoteExists) {
				return {
					isAllowed: true,
					explanation: "",
				};
			}

			return {
				isAllowed: false,
				explanation:
					"Es existiert bereits ein Lieferschein. Machen Sie diesen ungültig, um einen Neuen zu erstellen.",
			};
		}

		return {
			isAllowed: false,
			explanation: "Die Art des Auftrags wurde nicht erkannt.",
		};
	},
	fetchJobFiles: async (jobId: number) => {
		const { organization } = useCentralStore.getState();
		if (!jobId || !organization) {
			Logger.error("No job id provided");
			showNotification({
				message: "Fehler beim Abrufen von Auftragsdateien",
				type: "error",
			});
			return;
		}

		const { data: allFiles, error } = await supabase
			.from(SupabaseShareEntityViewEnum.FILES_WITH_SHARES)
			.select("*")
			.eq("job_id", jobId);

		if (error) {
			Logger.error(error, {}, "Error getting files");
			showNotification({
				message: "Fehler beim Abrufen der Dateien",
				type: "error",
			});
			return;
		}

		if (error) {
			Logger.error(error, {}, "Error fetching job files");
			showNotification({
				message: "Fehler beim Abrufen von Auftragsdateien",
				type: "error",
			});
		}

		await Promise.all(
			allFiles.map(async (d: FileWithShare) => {
				if (!d.path_name) return;
				const { data: imageData, error } = await supabase.storage
					.from("v1")
					.createSignedUrl(d.path_name, 3600);

				if (error) {
					Logger.error(error, {}, "Error getting url for file");
					showNotification({
						message: "Fehler beim Abrufen der URL für die Datei",
						type: "error",
					});
					return;
				}

				// @ts-expect-error image doesn't exist in data
				d.image = imageData?.signedUrl;
			})
		);

		set({
			labImages: allFiles.filter(
				(file) =>
					file.meta_data &&
					file.file_name &&
					getFileType(file.meta_data as Record<string, any>, file.file_name) == "img" &&
					file.organization_id == organization?.id
			),
			labFiles: allFiles.filter(
				(file) =>
					file.meta_data &&
					file.file_name &&
					getFileType(file.meta_data as Record<string, any>, file.file_name) != "img" &&
					file.organization_id == organization?.id
			),
			medImages: allFiles.filter(
				(file) =>
					file.meta_data &&
					file.file_name &&
					getFileType(file.meta_data as Record<string, any>, file.file_name) == "img" &&
					file.organization_id != organization?.id
			),
			medFiles: allFiles.filter(
				(file) =>
					file.meta_data &&
					file.file_name &&
					getFileType(file.meta_data as Record<string, any>, file.file_name) != "img" &&
					file.organization_id != organization?.id
			),
		});
	},
	shareFile: async (fileId: string, jobId: number) => {
		const { profile, organization } = useCentralStore.getState();
		if (!profile?.id) return;
		if (!organization?.id) return;

		const { error } = await supabase.rpc(SupabaseRpcEnum.SHARE_FILE, {
			p_share_file_id: fileId,
			p_message_content: {
				type: "shared_file",
				file_id: fileId,
				text: "",
			},
		});

		if (error) {
			Logger.error(error, {}, "Error sharing file");
			showNotification({
				message: "Fehler beim Teilen der Datei.",
				type: "error",
			});
		}

		get().fetchJobFiles(jobId);
	},
	unshareFile: async (fileId: string, jobId: number) => {
		const { profile, organization } = useCentralStore.getState();
		if (!profile?.id) return;
		if (!organization?.id) return;
		const { error } = await supabase.rpc(SupabaseRpcEnum.UNSHARE_FILE, {
			unshare_file_id: fileId,
		});

		if (error) {
			Logger.error(error, {}, "Error unsharing file");
			showNotification({
				message: "Fehler beim Entfernen der Freigabe der Datei.",
				type: "error",
			});
		}

		get().fetchJobFiles(jobId);
	},
});
